import { Badge, Box, Card, Divider, Heading, Text } from '@wix/design-system';
import { FunctionComponent, useContext, useEffect } from 'react';
import css from './index.module.scss';
import { InfoCircleSmall } from '@wix/wix-ui-icons-common';
import { GenAnswerFooter } from './GenAnswerFooter';
import { ArticleSearchResultItem, GenAiArticleLink } from '../../types';
import { AIMediumIcon } from '../../icons/AIMediumIcon';
import { DATA_HOOKS } from '../../dataHooks';
import { useTranslation } from '@wix/wix-i18n-config';
import { RenderHTML } from '../RenderHTML';
import { BI, KA_SEARCH_METHOD } from '../../constants';
import { useBI } from '../../hooks/useBI';
import { useRouter } from 'next/router';
import { clickOnAnswer } from '@wix/bi-logger-new-help-center/v2';
import { Context } from '../../context';
import classNames from 'classnames';
import { countWords } from '../../utils/text';
type GeneratedAnswerProps = {
  searchQuestion: string;
  searchAnswer: string;
  articleLinks: ArticleSearchResultItem[];
};
export enum unhelpfulFeedbackTypes {
  WRONG = 'wrong',
  IRRELEVANT = 'irrelevant',
  CONFUSING = 'confusing',
  HARD = 'hard',
  OUTDATED = 'outdated',
}
export const formatRichText = (text: string): string => {
  // replace **text** with<strong>text</strong>
  const boldRegex = /\*\*(.*?)\*\*/g;
  return text.replace(boldRegex, '<strong>$1</strong>');
};

export const GeneratedAnswer: FunctionComponent<GeneratedAnswerProps> = ({
  searchQuestion,
  searchAnswer,
  articleLinks
}) => {
  const { t } = useTranslation();
  const { sendBIEvent } = useBI();
  const { locale } = useRouter();
  const { searchSessionId } = useContext(Context);

  useEffect(() => {
    const anchors = document.querySelectorAll(`[data-hook=${DATA_HOOKS.GEN_ANSWER_ANSWER}] a`);

    const moreInfoArticlesToJSON = () =>
      articleLinks.map((articleLink) => ({
        article_id: articleLink.id,
        output_distance: articleLink.distance,
      })
      );

    const handleClickLink = async (anchor: Event) => {
      const linkURL = (anchor.target as HTMLElement).getAttribute('href');
      await sendBIEvent(
        clickOnAnswer({
          question: searchQuestion,
          source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
          kb_lang: locale as string,
          clicked_url: linkURL as string,
          clicked_item_type: BI.ITEM_TYPES.SUMMARIZED_ANSWER_HYPERLINK,
          search_result_selection:
            BI.SEARCH_RESULT_SELECTION.FULL_SEARCH_RESULTS_LIST,
          search_method: KA_SEARCH_METHOD,
          search_session_Id: searchSessionId,
          summarized_answer_based_on: JSON.stringify(moreInfoArticlesToJSON()),
          summarized_answer_words_count: countWords(searchAnswer)
        })
      );
    };

    anchors.forEach((anchor) => {
      anchor.addEventListener('click', handleClickLink);
    });

    return () => {
      anchors.forEach((anchor) => {
        anchor.removeEventListener('click', handleClickLink);
      });
    };
  }, []);

  const genArticleLinks = (topFiveResults: ArticleSearchResultItem[]): GenAiArticleLink[] => {
    const topResultsLinks: GenAiArticleLink[] = [];
    topFiveResults.forEach((result) => {
      topResultsLinks.push({
        articleId: result.id,
        articleName: result.title,
        articleURL: result.url,
        distance: result.distance,
      });
    });
    return topResultsLinks;
  };

  return (
    <Box className={css.nextGenAnswerWrapper} dataHook={DATA_HOOKS.GEN_ANSWER_WRAPPER}>
      <Box direction="vertical" className={css.nextGenAnswerInnerWrapper}>
        <Box direction="vertical">
          <Box direction="horizontal" className={css.genAnswerHeader}>
            <Box direction="horizontal" display='inline' className={css.headerText}>
              <Box className={css.aiIconWrapper}>
                <AIMediumIcon />
              </Box>
              <Heading dataHook={DATA_HOOKS.GEN_ANSWER_HEADING}>
                <Text className={css.aiAnswer}>
                  {t('search-results-page.gen-answer.AI-answer')}
                </Text>
                <Text dataHook={DATA_HOOKS.GEN_ANSWER_QUESTION} className={css.searchQuestion}>{`for "${searchQuestion}"`}</Text>
              </Heading>
            </Box>
            <Box className={css.betaDisclaimerWrapper} dataHook={DATA_HOOKS.BETA_GEN_ANSWER}>
              <Badge className={css.betaDisclaimer} uppercase={false} skin="neutralLight" type="outlined">
                {t('search-results-page.gen-answer.beta')}
              </Badge>
            </Box>
          </Box>

          <Card className={css.answerContainerCard}>
            <Text className={css.answerText} size='medium' dataHook={DATA_HOOKS.GEN_ANSWER_ANSWER} weight="thin">
              <RenderHTML html={formatRichText(searchAnswer)} />
            </Text>
            <Box direction="horizontal" className={classNames(css.infoWrapper, { [css.shortened]: articleLinks.length === 0 })}
              gap='3px' dataHook={DATA_HOOKS.GEN_ANSWER_LEGAL_INFO}>
              <InfoCircleSmall />
              <Text size='tiny' secondary >
                {t('search-results-page.gen-answer.legal-info')}
              </Text>
            </Box>
            {articleLinks.length > 0 &&
              <Divider className={css.verticalDivider} />}
            <GenAnswerFooter
              moreInfoArticles={genArticleLinks(articleLinks)}
              question={searchQuestion}
              answerWordCount={countWords(searchAnswer)}></GenAnswerFooter>
          </Card>
        </Box>
      </Box>
    </Box>);
};
